import { useEffect, useState } from 'react'
import api from '../api/axios'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  MinusCircleIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface Company {
  id: string
  company_name: string
  company_code: string
  database_name: string
}

interface FixAction {
  kind: string
  label: string
  description?: string
}

interface Check {
  id: string
  title: string
  status: 'pass' | 'warn' | 'fail' | 'skip'
  message: string
  details?: any
  fix?: FixAction | null
}

interface Result {
  companyCode: string
  databaseName: string
  ranAt: string
  summary: { overall: string; pass: number; warn: number; fail: number; skip: number; total: number }
  checks: Check[]
}

interface Props {
  company: Company
  onClose: () => void
}

const STATUS_META: Record<Check['status'], { icon: any; color: string; bg: string; label: string }> = {
  pass: { icon: CheckCircleIcon, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Pass' },
  warn: { icon: ExclamationTriangleIcon, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Warn' },
  fail: { icon: XCircleIcon, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Fail' },
  skip: { icon: MinusCircleIcon, color: 'text-slate-400', bg: 'bg-slate-50 border-slate-200', label: 'Skip' },
}

export default function HealthCheckModal({ company, onClose }: Props) {
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  const [error, setError] = useState('')
  const [fixing, setFixing] = useState<string | null>(null)
  const [fixMessages, setFixMessages] = useState<Record<string, { ok: boolean; msg: string }>>({})

  useEffect(() => {
    void run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const run = async () => {
    setRunning(true)
    setError('')
    setFixMessages({})
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/health-check`, {})
      setResult(data)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Health check failed')
    } finally {
      setRunning(false)
    }
  }

  const applyFix = async (checkId: string, fix: FixAction) => {
    if (fixing) return
    if (!confirm(`Apply fix: ${fix.label}?\n\n${fix.description || ''}`)) return
    setFixing(checkId)
    setFixMessages(prev => ({ ...prev, [checkId]: { ok: true, msg: 'Applying…' } }))
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/health-check/fix`, {
        fixKind: fix.kind,
      })
      setFixMessages(prev => ({
        ...prev,
        [checkId]: { ok: true, msg: data.message || 'Fix applied' },
      }))
      // Re-run the check suite to verify
      await run()
    } catch (e: any) {
      setFixMessages(prev => ({
        ...prev,
        [checkId]: { ok: false, msg: e.response?.data?.message || 'Fix failed' },
      }))
    } finally {
      setFixing(null)
    }
  }

  const overallBanner = () => {
    if (!result) return null
    const o = result.summary.overall
    const tone =
      o === 'healthy' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
      o === 'degraded' ? 'bg-amber-50 border-amber-200 text-amber-800' :
      'bg-red-50 border-red-200 text-red-800'
    return (
      <div className={`px-4 py-3 border rounded-lg text-sm flex items-center justify-between ${tone}`}>
        <div className="flex items-center gap-2">
          <HeartIcon className="w-5 h-5" />
          <span className="font-semibold uppercase tracking-wide">{o}</span>
          <span className="text-xs opacity-80">ran at {new Date(result.ranAt).toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span>{result.summary.pass} pass</span>
          <span>{result.summary.warn} warn</span>
          <span>{result.summary.fail} fail</span>
          <span>{result.summary.skip} skip</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !running && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl my-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <HeartIcon className="w-5 h-5 text-red-500" />
              Health check — {company.company_name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {company.company_code} · {company.database_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={run}
              disabled={running}
              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
            >
              <ArrowPathIcon className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
              Re-run
            </button>
            <button onClick={onClose} disabled={running} className="p-1 rounded hover:bg-slate-100 disabled:opacity-50">
              <XMarkIcon className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {running && !result && (
            <div className="p-8 text-center text-sm text-slate-500 flex flex-col items-center gap-3">
              <ArrowPathIcon className="w-8 h-8 text-blue-500 animate-spin" />
              Running {10} checks…
            </div>
          )}

          {result && (
            <>
              {overallBanner()}
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {result.checks.map(c => {
                  const meta = STATUS_META[c.status]
                  const Icon = meta.icon
                  const fixMsg = fixMessages[c.id]
                  return (
                    <div key={c.id} className={`px-4 py-3 flex items-start gap-3 ${meta.bg}`}>
                      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${meta.color}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                          {c.title}
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${meta.color}`}>{meta.label}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 font-mono whitespace-pre-wrap break-all">{c.message}</div>
                        {c.details && (
                          <details className="mt-1">
                            <summary className="text-[11px] text-slate-400 cursor-pointer">Details</summary>
                            <pre className="mt-1 text-[10px] text-slate-500 bg-white border border-slate-100 rounded p-2 overflow-auto max-h-48">
                              {JSON.stringify(c.details, null, 2)}
                            </pre>
                          </details>
                        )}
                        {c.fix && (
                          <div className="mt-2 flex items-start gap-2">
                            <button
                              onClick={() => applyFix(c.id, c.fix!)}
                              disabled={!!fixing || running}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-700 rounded disabled:opacity-50"
                              title={c.fix.description || ''}
                            >
                              {fixing === c.id ? (
                                <ArrowPathIcon className="w-3 h-3 animate-spin" />
                              ) : (
                                <WrenchScrewdriverIcon className="w-3 h-3" />
                              )}
                              {c.fix.label}
                            </button>
                            {c.fix.description && (
                              <span className="text-[11px] text-slate-500 leading-relaxed">{c.fix.description}</span>
                            )}
                          </div>
                        )}
                        {fixMsg && (
                          <div className={`mt-1.5 text-[11px] flex items-center gap-1 ${fixMsg.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                            {fixMsg.ok
                              ? <CheckCircleIcon className="w-3.5 h-3.5" />
                              : <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
                            {fixMsg.msg}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            disabled={running}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
